import { Link } from "react-router-dom";
import { ArrowRight, Check, Compass, Info } from "lucide-react";
import Reveal from "../components/Reveal";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";

const IMG = {
  marketing: "https://images.unsplash.com/photo-1763310225230-6e15b125935a?q=80&w=1200&auto=format&fit=crop",
  management: "https://images.unsplash.com/photo-1624555130581-1d9cca783bc0?q=80&w=1200&auto=format&fit=crop",
  assessments: "https://images.unsplash.com/photo-1786292949404-084cbd10c7b1?q=80&w=1200&auto=format&fit=crop",
};

export default function Services() {
  const { lang, t } = useLang();
  const base = `/${lang}`;

  return (
    <>
      <Seo title={t.meta.services.title} description={t.meta.services.description} />

      {/* Hero */}
      <section className="bg-paper-warm pb-14 pt-14 md:pb-20 md:pt-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <span className="h-px w-8 bg-teal-600" aria-hidden="true" />
              {t.services.hero.eyebrow}
            </p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl leading-[1.08] text-navy-900 sm:text-5xl lg:text-6xl" data-testid="services-title">
              {t.services.hero.title}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-navy-900/75">{t.services.hero.sub}</p>
          </Reveal>
        </div>
      </section>

      {/* Categories */}
      {t.services.categories.map((cat, i) => (
        <section key={cat.id} className={i % 2 === 0 ? "bg-paper-sand/60 py-16 md:py-24" : "bg-paper-warm py-16 md:py-24"} data-testid={`services-section-${cat.id}`}>
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16">
            <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <Reveal>
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-xl italic text-teal-600">{cat.index}</span>
                  <h2 className="font-display text-3xl leading-tight text-navy-900 md:text-4xl">{cat.name}</h2>
                </div>
                <p className="mt-3 font-display text-lg italic text-navy-900/60">{cat.tagline}</p>
                <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-navy-900/75">{cat.intro}</p>
              </Reveal>
              <Reveal delay={0.08}>
                <ul className="mt-8 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                  {cat.items.map((item) => (
                    <li key={item} className="flex gap-2.5 text-[15px] leading-snug text-navy-900/80">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to={`${base}/contact?interest=${cat.id}`} data-testid={`services-cta-${cat.id}`} className="btn-primary mt-9">
                  {cat.cta}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
            <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <Reveal delay={0.1}>
                <div className="relative">
                  <div className={`absolute h-full w-full rounded-[2rem] border border-teal-600/25 ${i % 2 === 1 ? "-left-4 -top-4" : "-right-4 -top-4"}`} aria-hidden="true" />
                  <img src={IMG[cat.id]} alt={cat.imgAlt} loading="lazy" className="relative aspect-[4/3] w-full rounded-[2rem] border border-navy-900/10 object-cover" />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      {/* Advisory note */}
      <section className="bg-paper-sand/60 pb-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div data-testid="services-advisory-note" className="flex items-start gap-4 rounded-2xl border-l-4 border-gold bg-paper-warm px-6 py-5 sm:px-8">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-navy-900/60" />
              <div>
                <h3 className="font-heading font-semibold text-navy-900">{t.services.advisory.title}</h3>
                <p className="mt-1 text-[15px] leading-relaxed text-navy-900/75">{t.services.advisory.body}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* UAE — compact */}
      <section className="bg-paper-warm pb-20 md:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-[2rem] border border-navy-900/10 bg-paper-sand/50 p-8 md:p-12" data-testid="services-uae-section">
              <div className="grid gap-10 lg:grid-cols-12">
                <div className="lg:col-span-5">
                  <p className="eyebrow">{t.services.uae.eyebrow}</p>
                  <h2 className="mt-3 font-display text-3xl leading-tight text-navy-900 md:text-4xl">{t.services.uae.title}</h2>
                  <p className="mt-4 text-[15px] leading-relaxed text-navy-900/70">{t.services.uae.intro}</p>
                  <Link to={`${base}/contact?interest=uae`} data-testid="services-uae-cta" className="btn-secondary mt-7">
                    {t.services.uae.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="lg:col-span-7">
                  <ul className="space-y-4">
                    {t.services.uae.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 rounded-xl border border-navy-900/10 bg-paper-warm px-5 py-4">
                        <Compass className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                        <span className="text-[15px] font-medium leading-snug text-navy-900">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 flex items-start gap-2 text-sm leading-relaxed text-navy-900/55">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    {t.services.uae.note}
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
